import { UserDTO } from '../state/user/user.dto';
import { PaginationDTO } from '../types/responses/responses-template';
import { BooleanNumber } from '../utilities/enums/boolean-number.enum';
import { TimezoneAbbreviation } from '../utilities/time-helpers';
import { ProfileLiteDTO } from './profile.dto';

export interface ReportRequestParams {
  dataset: string;
  dimension?: string;
  group?: string;
  complex_measure?: {
    type: string;
    numerator_measure: {
      type: string;
      field: string;
    };
    denominator_measure: {
      type: string;
      field: string;
    };
  };
  measure?: {
    type: string;
    field: string;
  };
  filters: KpiReportFilter[];
  debug?: boolean;
  id?: string;
  per_page?: number;
  page?: number;
  report?: string;
  report_filterset_id?: number;
}

export interface ReportSqlRequestParams {
  filters: KpiReportFilter[];
  report: string;
  dimension?: string;
  report_filterset_id?: number;
}

export interface QuickStatsReportRequestParams {
  report: string;
  action: string;
  filters: KpiReportFilter[];
  debug?: boolean;
  report_filterset_id?: number;
}

export interface HeatmapRequestParams {
  report: string;
  filters: KpiReportFilter[];
  report_filterset_id?: number;
  page?: number;
  per_page?: number;
}

export interface KpiReportFilter {
  codename: string;
  title?: string;
  value: string[];
  displayName?: string;
}

/**
 * Codenames stored inside a saved filterset (user-controlled global filters).
 * When report_filterset_id is present these are already on the backend — sending
 * them again as filters[] bloats GET URLs for no benefit.
 */
export const FILTERSET_CODENAMES = new Set([
  'district',
  'school_year',
  'school',
  'cohort',
  'session_type',
  'assigned_coach',
  'user',
  'form',
  'question_answer_submitted',
  'rubric',
  'competency',
  'pd_event',
  'pd_facilitator',
  'session_owner',
  'pd_session_type',
  'category',
  'category_option',
  'daterange',
  'keyword',
  'is_virtual',
  'pd_session_completed',
  'has_option',
  'is_reviewed',
]);

const filterSignature = (filter: KpiReportFilter): string =>
  `${filter.codename}:${JSON.stringify(filter.value)}`;

/**
 * Returns only the filters that are NOT already encoded in the filterset.
 * When reportFiltersetId is absent, returns the original array unchanged.
 *
 * Chart drilldowns reuse filterset codenames (e.g. `school`, `user`) as
 * per-request context that is not stored on the filterset. Pass the original
 * request's filters as `baseFilters` so those added filters are kept.
 */
export const extraFiltersOnly = (
  filters: KpiReportFilter[] | undefined,
  reportFiltersetId: number | undefined,
  baseFilters?: KpiReportFilter[]
): KpiReportFilter[] | undefined => {
  if (!reportFiltersetId || !filters?.length) return filters;
  const baseSignatures =
    baseFilters === undefined
      ? null
      : new Set(baseFilters.map(filterSignature));
  const extras = filters.filter((f) => {
    if (!FILTERSET_CODENAMES.has(f.codename)) return true;
    // Preserve callers that don't pass baseFilters: strip every filterset
    // codename, matching the original URL-bloat behavior.
    if (baseSignatures === null) return false;
    return !baseSignatures.has(filterSignature(f));
  });
  return extras.length ? extras : undefined;
};

export interface ReportApiResponse {
  datasets: ReportDataSeries[];
  denominator_count?: number;
  num_blank_form_submissions?: number;
  labels: string[];
  label_ids: number[];
  items?: TextResponse[];
  totalCount?: number;
  pageCount?: number;
  currentPage?: number;
  perPage?: number;
  series?: ReportDataSeries[];
  id?: string;
  pdSessionId?: number;
  labels_average?: number;
  labels_median?: number;
  labels_range?: number;
  valid?: boolean;
  error_messages?: string[];
  _meta?: PaginationDTO;
}

export interface GIGrowthDataResponse {
  filters: KpiReportFilter[];
  id: string;
  items: GIGrowthSchoolData[];
  yAxisType?: 'district' | 'school';
  _meta: PaginationDTO;
}

export interface GIGrowthSchoolData {
  items: GIGrowthUserData[];
  month1: number;
  month2: number;
  month3: number;
  month4: number;
  month5: number;
  month6: number;
  month7: number;
  month8: number;
  month9: number;
  month10: number;
  month11: number;
  month12: number;
  total?: number;
  school: string;
  school_id: string;
}

export interface GIGrowthUserData {
  month1: number;
  month2: number;
  month3: number;
  month4: number;
  month5: number;
  month6: number;
  month7: number;
  month8: number;
  month9: number;
  month10: number;
  month11: number;
  month12: number;
  total?: number;
  user_id: string;
  user_name: string;
}

export interface ReportDataSeries {
  label?: string;
  name?: string;
  data: number[] | ReportDataSeries[];
  dataNumerator?: number[];
  dataDenominator?: number[];
  originalIndex?: number;
}

export interface TextResponse {
  submission_date: string;
  field_text: string;
  coachlog_id: number;
  name: string;
  text_answer: string;
}

export interface SubmissionsResponse {
  submission_id: number;
  submission_date: string;
  user_name: string;
  email: string;
  grade: number;
}

export interface CalibrationAveragesResponse {
  id: number;
  filters: KpiReportFilter[];
  standards: string[];
  items: CalibrationAveragesItem[];
  _meta: PaginationDTO;
}

export interface CalibrationAveragesItem {
  user_name: string;
  user_id: number;
  profile: ProfileLiteDTO;
  total_average: number;
  standards: number[];
}

export interface LogShadower {
  shadower: string;
  shadowerPhoto: string;
  shadowerColor: string;
  shadowerUserId: number;
}

export interface ReportingLogDetailsItemDTO {
  id: number;
  title: string;
  dateAndTime: string;
  startDatetime: number;
  timezone: TimezoneAbbreviation;
  durationMins: string;
  logType: string;
  logTypeView: string;
  observationForm: string | null;
  notes: string;
  coachUserId: number;
  coach: string;
  coachPhoto: string;
  coachColor: string;
  attendeeUserId: number;
  attendee: string;
  attendeePhoto: string;
  attendeeColor: string;
  shadowers: LogShadower[];
  present: BooleanNumber;
  absentReason: string | null;
  school: string;
  attendeeDistrict: string;
  coachingSource: string;
  feedback: string;
  badges: string[];
  badgeCount: number;
  competencies: string[];
  giCount: number;
}

export interface ReportingPdCreditEarnedItemDTO {
  userName: string;
  userId: number;
  cohorts: string[];
  sessionCount: number;
  competencyCount: number;
  totalCreditsEarned: number;
  creditTypes: { [key: string]: number };
}

export interface ReportingPdSessionSummaryItemDTO {
  sessionName: string;
  pdSessionId: number;
  sessionDate: string;
  facilitators: UserDTO[];
  competencies: string[];
  sessionType: string;
  isCompleted: number;
  attendedCount: number;
  registeredCount: number;
  waitlistCount: number;
  percentPresent: number;
}
export interface ReportingPdSessionAttendanceItemDTO {
  userName: string;
  userId: number;
  pdSessionId: number;
  school: string;
  cohorts: string[];
  status: string;
  statusCode: number;
}
export interface ReportingGoalItemDTO {
  title: string;
  description: string;
  value: number;
}
